import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Users, Plus, Sparkles, Loader2, CheckCircle2, ChevronDown, Settings2, Video, PlayCircle, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GroupSessionManager = ({ session, onChange, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [zoomUrl, setZoomUrl] = useState(session.zoom_url || '');
  const [recordingUrl, setRecordingUrl] = useState(session.recording_url || '');
  const [notes, setNotes] = useState(session.notes || '');
  const [transcript, setTranscript] = useState(session.transcript || '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const patch = async (payload, msg) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/admin/group-sessions/${session.group_session_id}`, payload, { withCredentials: true });
      toast.success(msg || 'Saved');
      onChange?.(res.data);
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const generateAI = async () => {
    if (transcript.trim().length < 40) { toast.error('Paste a transcript (40+ chars) first'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/group-sessions/${session.group_session_id}/ai-notes`, { transcript }, { withCredentials: true });
      toast.success('Group recap generated & shared with all members');
      onChange?.(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI generation failed'); } finally { setGenerating(false); }
  };

  const takeaways = session.ai_key_takeaways || [];
  const actionPlan = session.ai_action_plan || [];

  return (
    <div className="border border-[#E2E8F0] rounded-lg p-4" data-testid={`group-session-row-${session.group_session_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-[#0A0F1C] truncate">{session.title}</div>
          <div className="text-sm text-[#475569] flex flex-wrap gap-2">
            {session.session_date && <span>{session.session_date}</span>}
            {session.session_time && <span>· {session.session_time}</span>}
            <span className={`text-xs px-2 py-0.5 rounded ${session.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-[#EFF6FF] text-[#1D4ED8]'}`}>{session.status}</span>
            {session.recording_url && <span className="text-xs text-[#1D4ED8]">Recording set</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => onDelete(session.group_session_id)} className="p-1.5 rounded text-red-500 hover:bg-red-50" title="Delete" data-testid={`delete-group-${session.group_session_id}`}><Trash2 size={15} /></button>
          <button onClick={() => setOpen((v) => !v)} className="text-sm font-medium text-[#1D4ED8] inline-flex items-center gap-1" data-testid={`manage-group-${session.group_session_id}`}>
            <Settings2 size={14} /> Manage <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#475569] mb-1.5 flex items-center gap-1.5"><Video size={13} /> Zoom join link</label>
              <div className="flex gap-2">
                <Input value={zoomUrl} onChange={(e) => setZoomUrl(e.target.value)} placeholder="https://zoom.us/j/..." data-testid={`group-zoom-${session.group_session_id}`} />
                <Button type="button" onClick={() => patch({ zoom_url: zoomUrl }, 'Zoom saved')} disabled={saving} className="btn-outline">Save</Button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#475569] mb-1.5 flex items-center gap-1.5"><PlayCircle size={13} /> Recording URL (YouTube/Vimeo/MP4)</label>
              <div className="flex gap-2">
                <Input value={recordingUrl} onChange={(e) => setRecordingUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid={`group-recording-${session.group_session_id}`} />
                <Button type="button" onClick={() => patch({ recording_url: recordingUrl }, 'Recording saved')} disabled={saving} className="btn-outline">Save</Button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#475569]">Status:</span>
            {session.status === 'completed' ? (
              <Button type="button" onClick={() => patch({ status: 'scheduled' }, 'Reopened')} disabled={saving} className="btn-outline text-xs h-7 px-3">Reopen</Button>
            ) : (
              <Button type="button" onClick={() => patch({ status: 'completed' }, 'Marked complete')} disabled={saving} className="btn-outline text-xs h-7 px-3">Mark complete</Button>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#475569] mb-1.5">Coach notes (shared with all members)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes every member will see..." data-testid={`group-notes-${session.group_session_id}`} />
            <Button type="button" onClick={() => patch({ notes }, 'Notes saved')} disabled={saving} className="btn-outline mt-2 text-xs h-8">Save notes</Button>
          </div>

          <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <label className="block text-xs font-semibold text-[#475569] mb-1.5 flex items-center gap-1.5"><Sparkles size={13} className="text-[#F97316]" /> Paste group transcript → AI recap, takeaways &amp; action plan</label>
            <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={4} placeholder="Paste the raw group session transcript here..." data-testid={`group-transcript-${session.group_session_id}`} />
            <Button type="button" onClick={generateAI} disabled={generating} className="btn-primary-blue mt-2 text-sm" data-testid={`group-generate-ai-${session.group_session_id}`}>
              {generating ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Generating…</> : <><Sparkles size={14} className="mr-1.5" /> Generate group recap</>}
            </Button>
            {(session.ai_summary || takeaways.length > 0) && (
              <div className="mt-4 pt-3 border-t border-[#E2E8F0] space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700"><CheckCircle2 size={13} /> Generated &amp; visible to all members</div>
                {session.ai_summary && <p className="text-sm text-[#475569] whitespace-pre-wrap">{session.ai_summary}</p>}
                {takeaways.length > 0 && <ul className="list-disc list-inside text-sm text-[#475569] space-y-1">{takeaways.map((t, i) => <li key={i}>{t}</li>)}</ul>}
                {actionPlan.length > 0 && (
                  <div className="text-sm text-[#475569]"><div className="font-medium text-[#0A0F1C] mb-1">Action plan:</div>
                    <ol className="list-decimal list-inside space-y-1">{actionPlan.map((a, i) => <li key={i}>{a.task || a}{a.why ? <span className="text-[#94A3B8]"> — {a.why}</span> : null}</li>)}</ol>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminGroupSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', session_date: '', session_time: '', duration: '60 min', description: '', zoom_url: '', recording_url: '' });
  const [creating, setCreating] = useState(false);

  const load = () => {
    axios.get(`${API_URL}/api/admin/group-sessions`, { withCredentials: true })
      .then((r) => setSessions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/group-sessions`, form, { withCredentials: true });
      toast.success('Group session created');
      setForm({ title: '', session_date: '', session_time: '', duration: '60 min', description: '', zoom_url: '', recording_url: '' });
      setShowCreate(false);
      load();
    } catch { toast.error('Could not create'); } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this group session?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/group-sessions/${id}`, { withCredentials: true });
      setSessions((prev) => prev.filter((s) => s.group_session_id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <AdminLayout title="Group Sessions">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="heading-2 text-2xl text-[#0A0F1C] flex items-center gap-2"><Users size={22} className="text-[#F97316]" /> Group Sessions</h1>
            <p className="text-sm text-[#475569]">Member-wide group calls. Every member sees the recording, recap, action plan &amp; goals.</p>
          </div>
          <Button onClick={() => setShowCreate((v) => !v)} className="btn-primary-blue inline-flex items-center gap-2" data-testid="new-group-session-btn"><Plus size={16} /> New group session</Button>
        </div>

        {showCreate && (
          <div className="border border-[#E2E8F0] rounded-xl p-5 bg-white space-y-3" data-testid="create-group-session-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-[#0A0F1C] mb-1">Title *</label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Group Mastermind — Scaling Offers" data-testid="group-title-input" /></div>
              <div><label className="block text-sm font-medium text-[#0A0F1C] mb-1">Duration</label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="60 min" /></div>
              <div><label className="block text-sm font-medium text-[#0A0F1C] mb-1">Date</label><Input type="date" value={form.session_date} onChange={(e) => setForm({ ...form, session_date: e.target.value })} data-testid="group-date-input" /></div>
              <div><label className="block text-sm font-medium text-[#0A0F1C] mb-1">Time</label><Input value={form.session_time} onChange={(e) => setForm({ ...form, session_time: e.target.value })} placeholder="6:00 PM" /></div>
              <div><label className="block text-sm font-medium text-[#0A0F1C] mb-1">Zoom link (optional)</label><Input value={form.zoom_url} onChange={(e) => setForm({ ...form, zoom_url: e.target.value })} placeholder="https://zoom.us/j/..." /></div>
              <div><label className="block text-sm font-medium text-[#0A0F1C] mb-1">Recording URL (optional)</label><Input value={form.recording_url} onChange={(e) => setForm({ ...form, recording_url: e.target.value })} placeholder="https://youtube.com/..." /></div>
            </div>
            <div><label className="block text-sm font-medium text-[#0A0F1C] mb-1">Description</label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What this group session covers..." /></div>
            <div className="flex gap-2">
              <Button onClick={create} disabled={creating} className="btn-primary-blue" data-testid="create-group-session-submit">{creating ? 'Creating…' : 'Create'}</Button>
              <Button onClick={() => setShowCreate(false)} className="btn-outline">Cancel</Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-[#94A3B8]"><Loader2 className="animate-spin inline" size={22} /></div>
        ) : sessions.length === 0 ? (
          <div className="border border-dashed border-[#E2E8F0] rounded-xl p-10 text-center text-[#94A3B8]" data-testid="no-group-sessions-admin">
            No group sessions yet. Create one — it will appear in every member's portal under My Sessions → Group Sessions.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-group-sessions-list">
            {sessions.map((s) => (
              <GroupSessionManager
                key={s.group_session_id}
                session={s}
                onChange={(updated) => setSessions((prev) => prev.map((x) => x.group_session_id === updated.group_session_id ? { ...x, ...updated } : x))}
                onDelete={remove}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
